import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  CHANNEL_COLOR_OPTIONS,
  ChannelColor,
  ColorMode,
  resolveChannelColorToken,
} from 'actions/agent_preferences/lib/channel_color_constants';
import { cssVarForChannelColor } from './channel_color_css_var';
import connect from 'components/lib/connect';
import DropdownMenu from 'components/common/dropdown_menu_v2';
import { EndpointTypes } from 'models/endpoint';
import RadioGroup from 'components/common/radio_group';
import SetChannelColorMode from 'actions/agent_preferences/set_channel_color_mode';
import UpdateChannelColor from 'actions/agent_preferences/update_channel_color';
import { useExecuteAction } from 'components/hooks/connect_hooks';

// Built per-render with literal t() calls so the strings stay statically extractable.
function buildColorModeOptions(t) {
  return [
    { label: t('No custom colors'), value: ColorMode.NONE },
    { label: t('Border colors per channel'), value: ColorMode.PER_CHANNEL },
  ];
}

function buildChannelLabels(t) {
  return {
    [EndpointTypes.CHAT]: t('Chat'),
    [EndpointTypes.CUSTOM_CHANNEL]: t('Custom Channel'),
    [EndpointTypes.EMAIL]: t('Email'),
    [EndpointTypes.FB_COMMENT]: t('Facebook Comment'),
    [EndpointTypes.INSTAGRAM_COMMENT]: t('Instagram Comment'),
    [EndpointTypes.INSTAGRAM_DIRECT]: t('Instagram Direct'),
    [EndpointTypes.SMS]: t('SMS'),
    [EndpointTypes.FB_MESSENGER]: t('Facebook Messenger'),
    [EndpointTypes.VOICE]: t('Voice'),
    [EndpointTypes.WHATSAPP]: t('WhatsApp'),
  };
}

function buildColorLabelByValue(t) {
  return {
    [ChannelColor.GREEN]: t('Green'),
    [ChannelColor.DARK_GREEN]: t('Dark Green'),
    [ChannelColor.EGG_SHELLEY_BLUE]: t('Blue'),
    [ChannelColor.BRAND_DARK_BLUE]: t('Dark Blue'),
    [ChannelColor.PURPLE]: t('Purple'),
    [ChannelColor.DARK_PURPLE]: t('Dark Purple'),
    [ChannelColor.RED]: t('Red'),
    [ChannelColor.ORANGE]: t('Orange'),
    [ChannelColor.YELLOW]: t('Yellow'),
    [ChannelColor.GRAY]: t('Gray'),
  };
}

const CHANNELS_IN_ORDER = Object.freeze([
  EndpointTypes.CHAT,
  EndpointTypes.SMS,
  EndpointTypes.EMAIL,
  EndpointTypes.VOICE,
  EndpointTypes.FB_MESSENGER,
  EndpointTypes.FB_COMMENT,
  EndpointTypes.WHATSAPP,
  EndpointTypes.INSTAGRAM_DIRECT,
  EndpointTypes.INSTAGRAM_COMMENT,
  EndpointTypes.CUSTOM_CHANNEL,
]);

export function ChannelColors({ channelColors, isCustomizeEnabled }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();

  const colorModeOptions = buildColorModeOptions(t);
  const channelLabels = buildChannelLabels(t);
  const colorDropdownOptions = buildColorDropdownOptions(t);

  const onSelect = useCallback(
    (channel, color) => {
      executeAction(UpdateChannelColor, { channel, color });
    },
    [executeAction]
  );

  const currentMode = isCustomizeEnabled ? ColorMode.PER_CHANNEL : ColorMode.NONE;

  const onModeChange = useCallback(
    event => {
      const mode = event.target.value;
      if (mode !== currentMode) executeAction(SetChannelColorMode, { mode });
    },
    [currentMode, executeAction]
  );

  return (
    <div>
      <RadioGroup data-aid="colorMode" onChange={onModeChange} options={colorModeOptions} value={currentMode} />

      {isCustomizeEnabled
        ? CHANNELS_IN_ORDER.map(channel => {
            const currentValue = resolveChannelColorToken(channelColors, channel);
            const label = channelLabels[channel];
            return (
              <ChannelRow key={channel}>
                <ChannelLabel>{label}</ChannelLabel>
                <DropdownWrapper>
                  <DropdownMenu
                    data-aid={`channelColor-${channel}`}
                    onSelect={color => onSelect(channel, color)}
                    options={colorDropdownOptions}
                    value={currentValue}
                  />
                </DropdownWrapper>
              </ChannelRow>
            );
          })
        : null}
    </div>
  );
}

ChannelColors.propTypes = {
  channelColors: PropTypes.object.isRequired,
  isCustomizeEnabled: PropTypes.bool.isRequired,
};

const ChannelColorsContainer = connect(mapStateToProps)(ChannelColors);

function mapStateToProps({ getProvider }) {
  const preferences = getProvider('agentPreferences').get();

  return {
    channelColors: preferences.channelColors || {},
    isCustomizeEnabled: !!preferences.customizeChannelColors,
  };
}

const ChannelRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`;

const ChannelLabel = styled.div`
  flex: 1;
`;

const DropdownWrapper = styled.div`
  flex: 0 0 200px;
  width: 200px;

  > * {
    width: 100%;
  }
`;

const ColorSwatch = styled.span`
  border: 1px solid var(--color-gray300);
  border-radius: 4px;
  display: inline-block;
  height: 16px;
  width: 16px;
`;

const ColorOptionLabel = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

// Each palette color rendered as a swatch beside its translated name for the dropdown.
function buildColorDropdownOptions(t) {
  const colorLabels = buildColorLabelByValue(t);
  return CHANNEL_COLOR_OPTIONS.map(option => ({
    value: option.value,
    label: (
      <ColorOptionLabel>
        <ColorSwatch style={{ backgroundColor: cssVarForChannelColor(option.value) }} />
        <span>{colorLabels[option.value]}</span>
      </ColorOptionLabel>
    ),
  }));
}

export default ChannelColorsContainer;
